/**
 * Footer - Premium footer for ForjaHub
 * Includes links, social media, feedback button, and "Seja um Vendedor"
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, LifeBuoy, BadgeCheck, Download, Share, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';

const BecomeSellerModal = lazy(() => import('@/components/marketplace/BecomeSellerModal'));



interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

export default function Footer() {
  const { user } = useAuth();
  const { effectiveRole, sellerType } = useRole();
  const navigate = useNavigate();
  
  // Botão "Seja um Vendedor" só aparece se:
  // 1. Usuário logado
  // 2. Role permitida (não visitante)
  // 3. Sem plano de vendedor ativo
  const canBecomeSeller = user && 
    ['student', 'partner', 'staff', 'admin'].includes(effectiveRole) &&
    sellerType === null;
  const [becomeSellerOpen, setBecomeSellerOpen] = useState(false);

  // PWA install state
  const [installModalPlatform, setInstallModalPlatform] = useState<'ios' | 'android' | 'desktop' | null>(null);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    setStandalone(isInStandaloneMode());
  }, []);

  const handleSupportRedirect = () => {
    toast.info('O canal de feedback foi descontinuado.', {
      description: 'Abra um ticket no Suporte para que possamos te ajudar diretamente.',
    });
    navigate('/comunidade?tab=suporte');
  };

  const handleInstallClick = async () => {
    // Try global prompt first (Chrome/Edge)
    const prompt = (window as any).__pwaInstallPrompt?.get?.() as BeforeInstallPromptEvent | null;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") {
        setStandalone(true);
        import('@/services/pwaInstalls').then(m => m.trackPwaInstall());
      }
      (window as any).__pwaInstallPrompt?.clear?.();
    } else if (isIOS()) {
      setInstallModalPlatform('ios');
    } else if (isAndroid()) {
      setInstallModalPlatform('android');
    } else {
      // Desktop browser without native prompt (Opera, Firefox, etc.)
      setInstallModalPlatform('desktop');
    }
  };

  return (
    <>
      <footer className="mt-8 border-t border-border/30 safe-area-pb">
        <div className="flex flex-col items-center gap-4 md:gap-6 px-4 py-6 md:py-8">
          {/* Logo + Year */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Flame className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Ecossistema Forja</span>
            <span className="text-xs">•</span>
            <span className="text-xs">2026</span>
          </div>

          {/* Links */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap justify-center">
            <Link 
              to="/termos-de-uso" 
              className="hover:text-foreground transition-colors touch-target-sm flex items-center justify-center"
            >
              Termos de Uso
            </Link>
            <span className="text-border">|</span>
            <Link 
              to="/privacidade" 
              className="hover:text-foreground transition-colors touch-target-sm flex items-center justify-center"
            >
              Privacidade
            </Link>
            {effectiveRole !== 'visitor' && user && (
              <>
                <span className="text-border">|</span>
                <Link 
                  to="/marketplace/regras-do-vendedor" 
                  className="hover:text-foreground transition-colors touch-target-sm flex items-center justify-center"
                >
                  Regras do Vendedor
                </Link>
              </>
            )}
            {!standalone && (
              <>
                <span className="text-border">|</span>
                <button
                  onClick={handleInstallClick}
                  className="hover:text-foreground transition-colors touch-target-sm flex items-center justify-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  Instalar App
                </button>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="w-full max-w-xs h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

          {/* Action Buttons - Stack vertically on mobile */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full max-w-xs sm:w-auto sm:max-w-none">
            {/* Become Seller Button - Only for student, partner, staff, admin */}
            {canBecomeSeller && (
              <Button
                variant="default"
                size="sm"
                className="gap-2 text-xs min-h-[44px] w-full sm:w-auto"
                onClick={() => setBecomeSellerOpen(true)}
              >
                <BadgeCheck className="w-3.5 h-3.5" />
                Seja um Vendedor
              </Button>
            )}

            {/* Support Button — replaces the discontinued Feedback channel */}
            {user && (
              <Button
                variant="default"
                size="sm"
                className="gap-2 text-xs min-h-[44px] w-full sm:w-auto"
                onClick={handleSupportRedirect}
              >
                <LifeBuoy className="w-3.5 h-3.5" />
                Falar com Suporte
              </Button>
            )}
          </div>
        </div>
      </footer>

      {/* Become Seller Modal - Only for allowed roles */}
      {canBecomeSeller && (
        <Suspense fallback={null}>
          <BecomeSellerModal open={becomeSellerOpen} onOpenChange={setBecomeSellerOpen} />
        </Suspense>
      )}

      {/* Install Instructions Modal — platform-aware */}
      <Dialog open={installModalPlatform !== null} onOpenChange={(open) => { if (!open) setInstallModalPlatform(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              Instalar ForjaHub
            </DialogTitle>
            <DialogDescription>
              {installModalPlatform === 'desktop'
                ? 'Instale o ForjaHub usando o menu do seu navegador.'
                : 'Siga os passos abaixo para instalar no seu dispositivo.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {installModalPlatform === 'ios' ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">1</div>
                  <p className="text-sm text-muted-foreground pt-1">
                    Toque no ícone <Share className="inline h-4 w-4 -mt-0.5 text-primary" /> <strong>Compartilhar</strong> na barra do navegador.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">2</div>
                  <p className="text-sm text-muted-foreground pt-1">
                    Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">3</div>
                  <p className="text-sm text-muted-foreground pt-1">
                    Confirme tocando em <strong>"Adicionar"</strong>. Pronto!
                  </p>
                </div>
              </>
            ) : installModalPlatform === 'desktop' ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">1</div>
                  <p className="text-sm text-muted-foreground pt-1">
                    Na barra de endereço, procure o ícone de <strong>instalação</strong> <Download className="inline h-4 w-4 -mt-0.5 text-primary" /> ou acesse o <strong>menu do navegador</strong> <MoreVertical className="inline h-4 w-4 -mt-0.5 text-primary" />.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">2</div>
                  <p className="text-sm text-muted-foreground pt-1">
                    Selecione <strong>"Instalar ForjaHub"</strong> ou <strong>"Instalar aplicativo"</strong>.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">3</div>
                  <p className="text-sm text-muted-foreground pt-1">
                    Confirme clicando em <strong>"Instalar"</strong>. Pronto!
                  </p>
                </div>
                <p className="text-xs text-muted-foreground/70 mt-2">
                  💡 Dica: No <strong>Chrome</strong> ou <strong>Edge</strong>, a instalação é com 1 clique. Outros navegadores podem variar.
                </p>
              </>
            ) : (
              <>
                <ol className="space-y-3 list-none p-0 m-0">
                  <li className="grid grid-cols-[2rem,1fr] gap-3 items-start min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">1</div>
                    <p className="text-sm text-muted-foreground pt-1 min-w-0 break-words">
                      Abra o menu <strong>⋮</strong> do Chrome.
                    </p>
                  </li>
                  <li className="grid grid-cols-[2rem,1fr] gap-3 items-start min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">2</div>
                    <p className="text-sm text-muted-foreground pt-1 min-w-0 break-words">
                      Toque em <strong>Compartilhar</strong> e procure por <strong>"Adicionar à tela inicial"</strong>, <strong>"Instalar app"</strong> ou <strong>"Criar atalho"</strong>.
                    </p>
                  </li>
                  <li className="grid grid-cols-[2rem,1fr] gap-3 items-start min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">3</div>
                    <p className="text-sm text-muted-foreground pt-1 min-w-0 break-words">
                      Confirme em <strong>"Instalar"</strong> ou <strong>"Adicionar"</strong>. Pronto!
                    </p>
                  </li>
                </ol>
                <p className="text-xs text-muted-foreground/70 mt-2">
                  💡 Se a opção não aparecer, atualize a página 1 vez, aguarde alguns segundos e tente novamente.
                </p>
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setInstallModalPlatform(null)}>
              Entendi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
